import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Badge } from "./components/ui/badge";
import { toast, Toaster } from "sonner";
import { 
  Code, 
  Database, 
  Server, 
  Globe, 
  Shield, 
  Zap, 
  Users, 
  Mail, 
  Phone, 
  MapPin,
  ChevronRight,
  CheckCircle,
  ExternalLink,
  Cloud,
  Network,
  HardDrive,
  Eye,
  LogsIcon,
  Subscript,
  ServerCogIcon,
  MessageCircleMore,
  BookMarked,
  Superscript
} from "lucide-react";
import logo from "./assets/logo.png"; // relative path
import logo_transparent from "./assets/logo_transparent.png"; // relative path

const BACKEND_URL = window.env?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}`;

// Navigation Component
const Navigation = () => {
  const location = useLocation();
  
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-r rounded-lg flex items-center justify-center">
              <img src={logo_transparent} alt="Logo" width="200" />
            </div>
            <span className="text-white font-bold text-xl">MibleX.NET</span>
          </Link>
          
          <div className="flex space-x-8">
            <Link 
              to="/" 
              className={`text-sm font-medium transition-colors hover:text-cyan-400 ${
                location.pathname === '/' ? 'text-cyan-400' : 'text-gray-300'
              }`}
            >
              Home
            </Link>
            <Link 
              to="/portfolio" 
              className={`text-sm font-medium transition-colors hover:text-cyan-400 ${
                location.pathname === '/portfolio' ? 'text-cyan-400' : 'text-gray-300'
              }`}
            >
              Portfolio
            </Link>
            <Link 
              to="/contact" 
              className={`text-sm font-medium transition-colors hover:text-cyan-400 ${
                location.pathname === '/contact' ? 'text-cyan-400' : 'text-gray-300'
              }`}
            >
              Contact
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Home Component
const Home = () => {
  const [stats, setStats] = useState({ unique_visitors: 0, total_visits: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats`);
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    
    fetchStats();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Hero Section */}
      <div className="pt-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-blue-500/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            {/* Logo Placeholder */}
            <div className="mx-auto w-96 h-96 rounded-2xl flex items-center justify-center mb-8">
              <img src={logo_transparent} alt="Logo" width="400" />
            </div>
            
            <br />
            
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Professional IT Solutions & Software Development
            </p>
            
            <p className="text-lg text-gray-400 mb-12 max-w-2xl mx-auto">
              Delivering cutting-edge technology solutions across healthcare, insurance, finance, and crypto industries with expertise in Microsoft technologies and modern web development.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/portfolio">
                <Button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-8 py-3 text-lg">
                  View Our Work
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-cyan-500 text-cyan-400 hover:bg-cyan-500 hover:text-white px-8 py-3 text-lg">
                  Get In Touch
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-24 bg-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Why Choose MibleX.NET?
            </h2>
            <p className="text-xl text-gray-400">
              Professional solutions with cutting-edge technology
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <Code className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Expert Development</h3>
              <p className="text-gray-400">
                Full-stack development with Microsoft technologies, React, Node.js, and modern frameworks
              </p>
            </div>
            
            <div className="text-center group">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Secure Solutions</h3>
              <p className="text-gray-400">
                Enterprise-grade security with best practices for data protection and compliance
              </p>
            </div>
            
            <div className="text-center group">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <Zap className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">High Performance</h3>
              <p className="text-gray-400">
                Optimized applications built for scale, speed, and reliability across all platforms
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      {/* <div className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-cyan-400 mb-2">5+</div>
              <div className="text-gray-400">Projects Deployed</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-cyan-400 mb-2">{stats.unique_visitors}</div>
              <div className="text-gray-400">Unique Visitors</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-cyan-400 mb-2">{stats.total_visits}</div>
              <div className="text-gray-400">Total Visits</div>
            </div>
          </div>
        </div>
      </div> */}
    </div>
  );
};

// Portfolio Component
const Portfolio = () => {
  const techStack = [
    {
      category: "Technology Stack",
      technologies: [".NET Framework", ".NET Core", ".NET", "ASP.NET", "MAUI"],
      icon: <Server className="h-6 w-6" />,
      color: "from-green-500 to-cyan-600"
    },
    {
      category: "Programming",
      technologies: ["C/C++", "C#", "T-SQL", "Python", "XML", "ADO.NET"],
      icon: <Subscript className="h-6 w-6" />,
      color: "from-blue-500 to-purple-600"
    },
    {
      category: "Scripting",
      technologies: ["PowerShell", "Bash", "VBA", "Google Apps Script"],
      icon: <Superscript className="h-6 w-6" />,
      color: "from-blue-500 to-teal-600"
    },
    {
      category: "Web Technologies", 
      technologies: ["Razor Pages", "Blazor", "JavaScript", "jQuery", "CSS3", "HTML5"],
      icon: <Globe className="h-6 w-6" />,
      color: "from-cyan-500 to-blue-600"
    },
    {
      category: "Frontend Frameworks",
      technologies: ["ASP.NET", "MVC", "React.js", "Angular.js", "TypeScript", "Tailwind CSS"],
      icon: <Code className="h-6 w-6" />,
      color: "from-purple-500 to-pink-600"
    },
    {
      category: "Backend & APIs",
      technologies: ["Node.js", "Python", "FastAPI", "REST APIs", "GraphQL", "gRPC"],
      icon: <Network className="h-6 w-6" />,
      color: "from-orange-500 to-red-600"
    },
    {
      category: "Databases",
      technologies: ["SQL Server", "MariaDB", "PostgreSQL", "Cassandra", "MongoDB", "Redis", "Entity Framework", "Dapper", "NHibernate"],
      icon: <Database className="h-6 w-6" />,
      color: "from-yellow-500 to-orange-600"
    },
    {
      category: "Cloud",
      technologies: ["AWS", "Microsoft Azure", "GCP"],
      icon: <Cloud className="h-6 w-6" />,
      color: "from-cyan-500 to-teal-600"
    },
    {
      category: "Monitoring",
      technologies: ["Grafana", "Prometheus", "Zabbix", "Uptime Kuma"],
      icon: <Eye className="h-6 w-6" />,
      color: "from-red-500 to-red-600"
    },
    {
      category: "Logging",
      technologies: ["ELK Stack", "Loki Stack", "Serilog", "NLog", "Filebeat", "XpoLog", "Greylog"],
      icon: <LogsIcon className="h-6 w-6" />,
      color: "from-teal-500 to-white-600"
    },
    {
      category: "Caching & Queues",
      technologies: ["MemoryCache", "Redis", "Polly", "RabbitMQ", "IBM Sphere"],
      icon: <BookMarked className="h-6 w-6" />,
      color: "from-teal-500 to-blue-600"
    },
    {
      category: "Virtualization",
      technologies: ["VMware ESXi", "VirtualBox", "KVM", "Docker", "Kubernetes", "OpenShift", "Rancher", "Lens", "Portainer"],
      icon: <ServerCogIcon className="h-6 w-6" />,
      color: "from-gray-500 to-brown-600"
    }
  ];

  const projects = [
    {
      title: "Healthcare Management System",
      client: "Romanian Healthcare Provider",
      industry: "Health",
      technologies: ["ASP.NET Core", "SQL Server", "Razor Pages", "Blazor", "Javascript", "Dapper"],
      description: "Complete patient management system with appointment scheduling, medical records, and billing integration."
    },
    {
      title: "Insurance Platform",
      client: "USA Insurance Company", 
      industry: "Insurance",
      technologies: [".NET", "VB", "SQL Server"],
      description: "Automated claims processing system with fraud detection and customer portal."
    },
    {
      title: "Financial Crypto Platform",
      client: "Romanian Financial Services",
      industry: "Finance & Crypto",
      technologies: [".NET", "Python", "Node.js", "SQL Server", "Entity Framework", "Redis"],
      description: "Real-time trading platform with cryptocurrency support and portfolio management."
    },
    {
      title: "University Platform",
      client: "Romanian University",
      industry: "Education",
      technologies: [".NET", "ReactJS", "ASP.NET Core", "SQL Server", "Entity Framework", "Dapper", "Redis", "RabbitMQ"],
      description: "Platform dedicated to teachers and students, that allows the use of flows like: enrollment of students, disciplines, grades, catalogues etc."
    },
    {
      title: "HR Management System",
      client: "Romanian Hospital",
      industry: "Health",
      technologies: [".NET", "Blazor", "ASP.NET Core", "SQL Server", "Entity Framework"],
      description: "A comprehensive software solution designed to streamline human resources operations and employee lifecycle management."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Our <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Portfolio</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Showcasing our expertise across multiple industries with cutting-edge technology solutions
          </p>
        </div>

        {/* Tech Stack */}
        <div className="mb-20">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">Technology Stack</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {techStack.map((stack, index) => (
              <Card key={index} className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 transition-all">
                <CardHeader>
                  <div className="flex items-center space-x-3">
                    <div className={`w-12 h-12 bg-gradient-to-r ${stack.color} rounded-lg flex items-center justify-center`}>
                      {stack.icon}
                    </div>
                    <CardTitle className="text-white">{stack.category}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {stack.technologies.map((tech, techIndex) => (
                      <Badge key={techIndex} variant="secondary" className="bg-slate-700 text-cyan-400">
                        {tech}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Projects */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-12 text-center">Featured Projects</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <Card key={index} className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 transition-all">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    {project.title}
                    <CheckCircle className="ml-2 h-5 w-5 text-green-400" />
                  </CardTitle>
                  <CardDescription className="text-cyan-400">
                    {project.client} • {project.industry}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-400 mb-4">{project.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {project.technologies.map((tech, techIndex) => (
                      <Badge key={techIndex} variant="outline" className="border-cyan-500 text-cyan-400">
                        {tech}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Contact Component
const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    description: '',
    captcha_answer: ''
  });
  const [captcha, setCaptcha] = useState({ question: '', session_id: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCaptcha();
  }, []);

  const fetchCaptcha = async () => {
    try {
      console.log('Fetching captcha from:', `${API}/captcha`);
      const response = await axios.get(`${API}/captcha`);
      console.log('Captcha response:', response.data);
      setCaptcha(response.data);
    } catch (error) {
      console.error('Error fetching captcha:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: error.config
      });
      toast.error(`Error loading security verification: ${error.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        description: '',
        captcha_answer: ''
      });
      fetchCaptcha(); // Get new captcha
    } catch (error) {
      const message = error.response?.data?.detail || 'Error sending message. Please try again.';
      toast.error(message);
      if (message.includes('captcha')) {
        fetchCaptcha(); // Get new captcha on captcha error
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Get In <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Touch</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Ready to start your next project? Let's discuss how we can help bring your ideas to life.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Mail className="mr-3 h-6 w-6 text-cyan-400" />
                  Email Us
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400">office.miblex.net@gmail.com</p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Phone className="mr-3 h-6 w-6 text-cyan-400" />
                  Call Us
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400">+40 72x xxx xxx (Romania)</p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <MapPin className="mr-3 h-6 w-6 text-cyan-400" />
                  Our Expertise
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p className="text-cyan-400">✓ Healthcare Solutions (Europe)</p>
                  <p className="text-cyan-400">✓ Insurance Platforms (USA)</p>
                  <p className="text-cyan-400">✓ Financial & Crypto Systems (Romania)</p>
                  <p className="text-cyan-400">✓ University Platform (Romania)</p>
                  <p className="text-cyan-400">✓ HR Management System (Romania)</p>
                  <p className="text-cyan-400">✓ Custom Enterprise Software</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Send us a message</CardTitle>
              <CardDescription className="text-gray-400">
                We'll get back to you within 24 hours
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Input
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-gray-400"
                  />
                </div>
                
                <div>
                  <Input
                    name="email"
                    type="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-gray-400"
                  />
                </div>
                
                <div>
                  <Input
                    name="phone"
                    placeholder="Your Phone Number"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-gray-400"
                  />
                </div>
                
                <div>
                  <Textarea
                    name="description"
                    placeholder="Describe your project or requirements..."
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-gray-400"
                  />
                </div>
                
                {/* Captcha */}
                <div className="border border-slate-600 rounded-lg p-4 bg-slate-700/50">
                  <p className="text-white mb-2">Security Verification:</p>
                  <p className="text-cyan-400 mb-3">{captcha.question}</p>
                  <Input
                    name="captcha_answer"
                    placeholder="Your answer"
                    value={formData.captcha_answer}
                    onChange={handleInputChange}
                    required
                    className="bg-slate-600 border-slate-500 text-white"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white"
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Navigation />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/portfolio" element={<Portfolio />} />
          <Route path="/contact" element={<Contact />} />
        </Routes>
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;